import { useEffect, useState } from "react";
import { isInsideTeams } from "./useOnline";
import {
  awaitPlatformAuthHandoff,
  fetchAuthKitSession,
  isPlatformLaunch,
  readPlatformSession,
} from "@/lib/platformAuth";

export type BrowserAuthState = "checking" | "authenticated" | "unauthenticated";

/**
 * For browser (non-Teams) sessions, verifies the user is signed in via
 * Auth-Kit cookie SSO or Azure Easy Auth.
 *
 * - Inside Teams: immediately returns "authenticated" — Teams SSO (useTeamsAuth)
 *   handles identity; no Easy Auth session check is needed.
 * - On ka.nationaldatacare.com (shared Auth-Kit cookie): exchanges the
 *   `ndc-authenticated` marker for a WorkOS token via Auth-Kit session-token
 *   and skips Easy Auth when that succeeds.
 * - Platform Hub new-tab launch (fallback): waits for postMessage handoff.
 * - Outside Teams: calls /.auth/me.
 *   Unauthenticated → "unauthenticated" so the app can show a sign-in page
 *   (does not auto-redirect to Microsoft login).
 *   /.auth/me returns 404 (Easy Auth not configured, e.g. local dev) →
 *   "authenticated" so local Vite workflows still work.
 *
 * Fail-open only when Easy Auth is absent (404).
 *
 * This approach keeps Azure Easy Auth set to "Allow unauthenticated requests",
 * which lets the Teams iframe load without being blocked by a 302 redirect.
 * The browser auth wall is enforced here in the client instead.
 */
export function useBrowserAuth(): BrowserAuthState {
  const [state, setState] = useState<BrowserAuthState>(() =>
    isInsideTeams() ? "authenticated" : "checking",
  );

  useEffect(() => {
    if (isInsideTeams()) return;

    let cancelled = false;

    async function check() {
      if (readPlatformSession()?.accessToken) {
        setState("authenticated");
        return;
      }

      // Shared cookie on *.nationaldatacare.com (primary Platform SSO path).
      const cookieSession = await fetchAuthKitSession();
      if (cancelled) return;
      if (cookieSession?.accessToken) {
        setState("authenticated");
        return;
      }

      // Fallback: Hub opener postMessage handoff (pre-domain or cookie miss).
      if (isPlatformLaunch()) {
        const platform = await awaitPlatformAuthHandoff();
        if (cancelled) return;
        if (platform?.accessToken) {
          setState("authenticated");
          return;
        }
      }

      try {
        const res = await fetch("/.auth/me");
        if (cancelled) return;

        if (res.status === 404) {
          setState("authenticated");
          return;
        }

        if (res.status === 401 || !res.ok) {
          setState("unauthenticated");
          return;
        }

        const data = (await res.json()) as unknown;
        if (cancelled) return;

        if (Array.isArray(data) && data.length > 0) {
          setState("authenticated");
        } else {
          setState("unauthenticated");
        }
      } catch {
        if (!cancelled) setState("unauthenticated");
      }
    }

    void check();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
